import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Modal,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DocumentScannerOutlinedIcon from "@mui/icons-material/DocumentScannerOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ScreenShareOutlinedIcon from "@mui/icons-material/ScreenShareOutlined";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SaveSummary, ExtractMedicineSummaryV1 } from "../../connections/postApi";
import { s3UploadOCR, getOCRFiles } from "../../connections/s3UploadOCR";
import { getPatientAiSummary } from "../../connections/getApi";
import {
  initJob,
  setFileStatus,
  setFileKey,
  setFileDone,
  setFileError,
  setJobStatus,
  setJobDone,
  setJobError,
  clearJob,
} from "../../redux/slices/ocrSlice";

const MAX_FILES = 15;

const OCR_PROMPT =
  "You are a medical document OCR assistant. Extract all visible text from this medical document exactly as written. Then provide a structured summary including: medications with exact dosages and frequencies, diagnoses and conditions, lab or investigation results with numeric values, vital signs, and any doctor instructions or follow-up notes. Preserve all numeric values precisely. If text is partially legible, indicate uncertainty in brackets.";

const formatKey = (key) => key.replace(/_/g, " ");

const SummarySection = ({ label, value }) => {
  if (value === null || value === undefined) return null;

  const renderValue = () => {
    if (typeof value === "string") {
      return (
        <Typography sx={{ fontSize: "12px", color: "#374151", lineHeight: 1.7 }}>
          {value}
        </Typography>
      );
    }

    if (Array.isArray(value)) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {value.map((item, i) => (
            <Box key={i} sx={{ pl: 1.5, borderLeft: "2px solid #e5e7eb" }}>
              {typeof item === "string" ? (
                <Typography sx={{ fontSize: "12px", color: "#374151" }}>{item}</Typography>
              ) : (
                Object.entries(item)
                  .filter(([, v]) => v !== null && v !== undefined)
                  .map(([k, v]) => (
                    <Box key={k} sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", flexShrink: 0 }}>
                        {formatKey(k)}:
                      </Typography>
                      <Typography sx={{ fontSize: "11px", color: "#374151" }}>{String(v)}</Typography>
                    </Box>
                  ))
              )}
            </Box>
          ))}
        </Box>
      );
    }

    if (typeof value === "object") {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {Object.entries(value)
            .filter(([, v]) => v !== null && v !== undefined)
            .map(([k, v]) => (
              <Box key={k} sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", flexShrink: 0 }}>
                  {formatKey(k)}:
                </Typography>
                <Typography sx={{ fontSize: "11px", color: "#374151" }}>{String(v)}</Typography>
              </Box>
            ))}
        </Box>
      );
    }

    return (
      <Typography sx={{ fontSize: "12px", color: "#374151" }}>{String(value)}</Typography>
    );
  };

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "neutral.teal", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5 }}>
        {formatKey(label)}
      </Typography>
      {renderValue()}
    </Box>
  );
};

// ── per-file overlay ──────────────────────────────────────────────────────────
const FileOverlay = ({ status }) => {
  if (status === "pending") return null;

  const overlayColor =
    status === "done"
      ? "rgba(0,150,100,0.55)"
      : status === "error"
        ? "rgba(200,0,0,0.45)"
        : "rgba(0,0,0,0.45)";

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        borderRadius: "6px",
        backgroundColor: overlayColor,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.4,
      }}
    >
      {status === "done" && (
        <CheckCircleOutlineIcon sx={{ color: "#fff", fontSize: 26 }} />
      )}
      {status === "error" && (
        <ErrorOutlineIcon sx={{ color: "#fff", fontSize: 26 }} />
      )}
      {(status === "uploading" || status === "parsing") && (
        <>
          <CircularProgress size={20} sx={{ color: "#fff" }} />
          <Typography sx={{ color: "#fff", fontSize: "9px", fontWeight: 700, mt: 0.2 }}>
            {status === "uploading" ? "Uploading" : "Reading..."}
          </Typography>
        </>
      )}
    </Box>
  );
};

// ── status badge for background-reopened view ─────────────────────────────────
const statusChipStyle = {
  pending:   { bg: "#f3f4f6", color: "#6b7280" },
  uploading: { bg: "#eff6ff", color: "#3b82f6" },
  parsing:   { bg: "#fefce8", color: "#ca8a04" },
  done:      { bg: "#f0fdf4", color: "#16a34a" },
  error:     { bg: "#fef2f2", color: "#dc2626" },
};

const StatusBadge = ({ status }) => {
  const s = statusChipStyle[status] || statusChipStyle.pending;
  const label = { pending: "Waiting", uploading: "Uploading", parsing: "Reading", done: "Done", error: "Failed" }[status] || status;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        px: 1,
        py: 0.2,
        borderRadius: "20px",
        backgroundColor: s.bg,
        color: s.color,
        fontSize: "11px",
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {(status === "uploading" || status === "parsing") && (
        <CircularProgress size={9} sx={{ color: s.color }} />
      )}
      {label}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const OCRModal = ({
  onClose,
  ocrConfig = {},
  patientId,
  appointmentId,
  onResultsReady,
  patientName = "",
}) => {
  const ip = ocrConfig?.ip || process.env.REACT_APP_OCR_IP;
  const apiKey = ocrConfig?.api_key || process.env.REACT_APP_OCR_API_KEY;

  const dispatch = useDispatch();
  const jobKey = `${patientId}_${appointmentId}`;
  const job = useSelector((s) => s.ocr.jobs[jobKey]);

  const [activeTab, setActiveTab] = useState(0);

  // local scan state — only valid for the current component instance
  const [imageFiles, setImageFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState("");
  const fileInputRef = useRef(null);
  // guard: prevents double-triggering handleAnalyse
  const processingRef = useRef(false);

  // ── history tab ──
  const [historyImages, setHistoryImages] = useState([]);
  const [historySummary, setHistorySummary] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyFetched, setHistoryFetched] = useState(false);

  // ── image viewer ──
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);
  const openViewer = (file) => { setViewerFile(file); setViewerOpen(true); };
  const closeViewer = () => { setViewerOpen(false); setViewerFile(null); };

  useEffect(() => {
    if (activeTab === 1 && !historyFetched && appointmentId) fetchHistory();
  }, [activeTab]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const [files, summaryRes] = await Promise.all([
        getOCRFiles("Appointment", appointmentId, "medical_file"),
        getPatientAiSummary(patientId, appointmentId, "Appointment", "medical_file"),
      ]);
      setHistoryImages(files);
      setHistorySummary(summaryRes?.payload?.summary || null);
      setHistoryFetched(true);
    } catch {
      setHistoryError("Failed to load history.");
    } finally {
      setHistoryLoading(false);
    }
  };

  // ── file helpers ──
  const addFiles = (rawFiles) => {
    const valid = Array.from(rawFiles).filter((f) => f.type.startsWith("image/"));
    if (!valid.length) {
      setLocalError("Please upload image files (JPG, PNG, WEBP, BMP).");
      return;
    }
    setLocalError("");
    setImageFiles((prev) => {
      const combined = [
        ...prev,
        ...valid.map((f) => ({
          file: f,
          preview: URL.createObjectURL(f),
          name: f.name,
        })),
      ];
      if (combined.length > MAX_FILES) {
        setLocalError(`Maximum ${MAX_FILES} files allowed.`);
        return combined.slice(0, MAX_FILES);
      }
      return combined;
    });
  };

  const removeFile = (index) => {
    setImageFiles((prev) => {
      URL.revokeObjectURL(prev[index]?.preview);
      return prev.filter((_, i) => i !== index);
    });
    setLocalError("");
  };

  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const ocrParse = async (file) => {
    const dataUrl = await fileToDataUrl(file);
    const res = await fetch(`https://${ip}/v1/chat/completions`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: "medgemma",
        messages: [
          {
            role: "user",
            content: [
              { type: "text", text: OCR_PROMPT },
              { type: "image_url", image_url: { url: dataUrl } },
            ],
          },
        ],
        max_tokens: 800,
      }),
    });
    if (!res.ok) throw new Error(`OCR server error ${res.status}`);
    const data = await res.json();
    return data?.choices?.[0]?.message?.content || null;
  };

  // ── main processing pipeline ──
  const handleAnalyse = async () => {
    if (!imageFiles.length) { setLocalError("Please upload at least one image."); return; }
    if (!patientId || !appointmentId) { setLocalError("Patient and appointment must be selected."); return; }
    if (processingRef.current) return;

    processingRef.current = true;
    setLocalError("");

    dispatch(initJob({ jobKey, patientId, appointmentId, patientName, files: imageFiles }));

    const fileKeys = [];
    const ocrTexts = [];

    for (let i = 0; i < imageFiles.length; i++) {
      const entry = imageFiles[i];
      try {
        // Step 1 — upload to S3
        dispatch(setFileStatus({ jobKey, fileIdx: i, status: "uploading" }));
        const { key } = await s3UploadOCR(
          entry.file,
          "Appointment",
          appointmentId,
          "medical_file",
          "attachment",
        );
        dispatch(setFileKey({ jobKey, fileIdx: i, fileKey: key }));
        fileKeys.push(key);

        // Step 2 — MedGemma parse
        dispatch(setFileStatus({ jobKey, fileIdx: i, status: "parsing" }));
        const ocrText = await ocrParse(entry.file);
        const finalText = ocrText || "No response from model.";
        ocrTexts.push(finalText);

        dispatch(setFileDone({ jobKey, fileIdx: i, ocrText: finalText }));
      } catch (err) {
        const msg = err?.message || "Failed to process.";
        dispatch(setFileError({ jobKey, fileIdx: i, error: msg }));
        ocrTexts.push("");
        fileKeys.push(null);
      }
    }

    // Step 3 — extraction summary with ALL parsed texts
    const validTexts = ocrTexts.filter(Boolean);
    const primaryKey = fileKeys.find(Boolean);

    if (!validTexts.length || !primaryKey) {
      dispatch(setJobError({ jobKey, error: "All files failed to process. Please try again." }));
      processingRef.current = false;
      return;
    }

    dispatch(setJobStatus({ jobKey, status: "extracting" }));

    try {
      const result = await ExtractMedicineSummaryV1(
        primaryKey,
        "ocr_text_summary",
        "Appointment",
        appointmentId,
        patientId,
        validTexts,
      );
      const extractionPayload = result?.payload ?? result;
      SaveSummary({
        patient_id: patientId,
        resource_id: appointmentId,
        resource_type: "Appointment",
        transcription: extractionPayload,
        summary_type: "medical_file",
      }).catch(console.error);
      dispatch(setJobDone({ jobKey, extractionResult: extractionPayload }));
      setHistoryFetched(false);
      if (onResultsReady) {
        onResultsReady(
          ocrTexts.map((t, i) => ({
            name: imageFiles[i]?.name || `File ${i + 1}`,
            content: t || null,
            error: t ? null : "Failed",
          })),
        );
      }
    } catch (err) {
      dispatch(setJobError({ jobKey, error: err?.message || "Extraction failed." }));
    }

    processingRef.current = false;
  };

  const handleReset = () => {
    if (["processing", "extracting"].includes(job?.status)) return;
    dispatch(clearJob({ jobKey }));
    imageFiles.forEach((f) => URL.revokeObjectURL(f.preview));
    setImageFiles([]);
    setLocalError("");
    processingRef.current = false;
  };

  // ── derived state ──
  const isActive = ["processing", "extracting"].includes(job?.status);
  const isDone = job?.status === "done";
  const isError = job?.status === "error";
  const hasJob = !!job;

  const totalFiles = job?.files?.length ?? 0;
  const doneFiles = job?.files?.filter((f) => ["done", "error"].includes(f.status)).length ?? 0;
  const progressPct = totalFiles ? Math.round((doneFiles / totalFiles) * 100) : 0;

  // are we in the same component instance that started the job?
  const hasPreviews = imageFiles.length === totalFiles && totalFiles > 0;

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ minHeight: 360, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, pt: 2, pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DocumentScannerOutlinedIcon sx={{ color: "neutral.teal", fontSize: 22 }} />
          <Typography fontWeight={700} fontSize="16px">
            Medical Image Analysis
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          px: 2,
          minHeight: 38,
          "& .MuiTabs-indicator": { backgroundColor: "neutral.teal" },
          "& .MuiTab-root": {
            minHeight: 38,
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 600,
            color: "#6b7280",
            "&.Mui-selected": { color: "neutral.teal" },
          },
        }}
      >
        <Tab icon={<DocumentScannerOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Scan" />
        <Tab icon={<HistoryOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="History" />
      </Tabs>

      <Box sx={{ px: 2, pb: 2, pt: 1.5, flex: 1, overflowY: "auto" }}>

        {/* ══ SCAN TAB ══ */}
        {activeTab === 0 && (
          <>
            {/* ── ACTIVE JOB (processing or extracting) ── */}
            {isActive && (
              <Box>
                {/* progress bar */}
                <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography sx={{ fontSize: "12px", color: "#6b7280", fontWeight: 600 }}>
                      {job.status === "extracting"
                        ? "Generating AI Summary…"
                        : `Processing ${doneFiles} / ${totalFiles} files`}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "neutral.teal", fontWeight: 700 }}>
                      {job.status === "extracting" ? "100%" : `${progressPct}%`}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant={job.status === "extracting" ? "indeterminate" : "determinate"}
                    value={progressPct}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: "#e5e7eb",
                      "& .MuiLinearProgress-bar": { backgroundColor: "neutral.teal", borderRadius: 3 },
                    }}
                  />
                </Box>

                {/* file thumbnails (same session) OR file list (reopened) */}
                {hasPreviews ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                    {imageFiles.map((entry, i) => (
                      <Box
                        key={i}
                        sx={{ position: "relative", width: 80, height: 80, flexShrink: 0 }}
                      >
                        <img
                          src={entry.preview}
                          alt={entry.name}
                          style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 6, border: "1px solid #e0e0e0" }}
                        />
                        <FileOverlay status={job.files[i]?.status ?? "pending"} />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: 1.5 }}>
                    {job.files.map((f, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: 1.5,
                          py: 0.75,
                          borderRadius: "8px",
                          backgroundColor: "#f9fafb",
                          border: "1px solid #e5e7eb",
                          gap: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: "12px", color: "#374151", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {f.name}
                        </Typography>
                        <StatusBadge status={f.status} />
                      </Box>
                    ))}
                  </Box>
                )}

                {job.status === "extracting" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1.5,
                      borderRadius: "10px",
                      backgroundColor: "#f0fdf4",
                      border: "1px solid #bbf7d0",
                    }}
                  >
                    <CircularProgress size={16} sx={{ color: "#16a34a" }} />
                    <Typography sx={{ fontSize: "12px", color: "#15803d", fontWeight: 600 }}>
                      All files processed — generating consolidated AI summary…
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* ── DONE ── */}
            {isDone && (
              <Box>
                {/* extraction result */}
                {job.extractionResult && (
                  <Box
                    sx={{
                      p: 2,
                      mb: 2,
                      backgroundColor: "#f7fbfb",
                      borderRadius: "10px",
                      border: "1px solid #ddeeed",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "neutral.teal" }} />
                      <Typography sx={{ color: "neutral.teal", fontWeight: 700, fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: 0.4 }}>
                        AI Clinical Summary
                      </Typography>
                    </Box>
                    {(() => {
                      const r = job.extractionResult;
                      const summaries = r?.summaries;
                      if (summaries && typeof summaries === "object" && !Array.isArray(summaries)) {
                        return Object.entries(summaries).map(([key, val]) => (
                          <SummarySection key={key} label={key} value={val} />
                        ));
                      }
                      return (
                        <Typography variant="body2" sx={{ color: "#1F2937", lineHeight: 1.8, whiteSpace: "pre-wrap", fontFamily: "Lato" }}>
                          {typeof r === "string" ? r : JSON.stringify(r, null, 2)}
                        </Typography>
                      );
                    })()}
                  </Box>
                )}

                {/* per-file breakdown */}
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4, mb: 0.75 }}>
                    Files processed ({job.files.length})
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {job.files.map((f, i) => (
                      <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.3, borderRadius: "20px", backgroundColor: f.status === "error" ? "#fef2f2" : "#f0fdf4", border: `1px solid ${f.status === "error" ? "#fecaca" : "#bbf7d0"}` }}>
                        {f.status === "error"
                          ? <ErrorOutlineIcon sx={{ fontSize: 12, color: "#dc2626" }} />
                          : <CheckCircleOutlineIcon sx={{ fontSize: 12, color: "#16a34a" }} />}
                        <Typography sx={{ fontSize: "11px", color: f.status === "error" ? "#dc2626" : "#15803d", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {f.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleReset}
                  sx={{ textTransform: "none", borderColor: "neutral.teal", color: "neutral.teal", borderRadius: "8px", fontSize: "12px" }}
                >
                  Scan New Files
                </Button>
              </Box>
            )}

            {/* ── ERROR ── */}
            {isError && (
              <Box>
                <Box sx={{ p: 2, mb: 2, backgroundColor: "#fff5f5", borderRadius: "10px", border: "1px solid #ffc9c9" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                    <ErrorOutlineIcon sx={{ fontSize: 16, color: "#c62828" }} />
                    <Typography sx={{ fontWeight: 700, fontSize: "13px", color: "#c62828" }}>Processing failed</Typography>
                  </Box>
                  <Typography sx={{ fontSize: "12px", color: "#c62828" }}>{job.error}</Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleReset}
                  sx={{ textTransform: "none", borderColor: "#dc2626", color: "#dc2626", borderRadius: "8px", fontSize: "12px" }}
                >
                  Try Again
                </Button>
              </Box>
            )}

            {/* ── UPLOAD UI (no job, or after reset) ── */}
            {!hasJob && (
              <>
                {/* drop zone */}
                <Box
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files); }}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    border: `2px dashed ${dragging ? "#007a99" : "#b0bec5"}`,
                    borderRadius: "10px",
                    p: 2,
                    mb: 1.5,
                    textAlign: "center",
                    cursor: "pointer",
                    backgroundColor: dragging ? "rgba(0,122,153,0.05)" : "#fafafa",
                    transition: "all 0.2s",
                    minHeight: imageFiles.length ? "auto" : 120,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ""; }}
                  />

                  {imageFiles.length > 0 ? (
                    <>
                      <Box
                        sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", width: "100%" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {imageFiles.map((entry, i) => (
                          <Box key={i} sx={{ position: "relative", width: 80, height: 80, flexShrink: 0 }}>
                            <img
                              src={entry.preview}
                              alt={entry.name}
                              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 6, border: "1px solid #e0e0e0" }}
                            />
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                              sx={{ position: "absolute", top: -6, right: -6, backgroundColor: "#fff", border: "1px solid #e0e0e0", p: "2px", "&:hover": { backgroundColor: "#fde8e8" } }}
                            >
                              <CloseIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                          </Box>
                        ))}

                        {imageFiles.length < MAX_FILES && (
                          <Box
                            sx={{ width: 80, height: 80, border: "2px dashed #b0bec5", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, "&:hover": { backgroundColor: "rgba(0,122,153,0.05)", borderColor: "#007a99" } }}
                            onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                          >
                            <FileUploadOutlinedIcon sx={{ fontSize: 24, color: "#90a4ae" }} />
                          </Box>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        {imageFiles.length}/{MAX_FILES} images selected
                      </Typography>
                    </>
                  ) : (
                    <>
                      <FileUploadOutlinedIcon sx={{ fontSize: 36, color: "#90a4ae" }} />
                      <Typography variant="body2" color="text.secondary">Drag & drop or click to upload medical images</Typography>
                      <Typography variant="caption" color="text.disabled">JPG, PNG, WEBP, BMP · Up to {MAX_FILES} files</Typography>
                    </>
                  )}
                </Box>

                {localError && (
                  <Typography variant="body2" color="error" sx={{ mb: 1, p: 1, backgroundColor: "#fff5f5", borderRadius: "6px", fontSize: "12px" }}>
                    {localError}
                  </Typography>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  disabled={!imageFiles.length}
                  onClick={handleAnalyse}
                  sx={{ backgroundColor: "neutral.teal", textTransform: "none", borderRadius: "8px", fontWeight: 600, "&:hover": { backgroundColor: "neutral.tealDark" } }}
                >
                  Analyse {imageFiles.length > 1 ? `${imageFiles.length} Images` : "Image"}
                </Button>
              </>
            )}
          </>
        )}

        {/* ══ HISTORY TAB ══ */}
        {activeTab === 1 && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 600, fontSize: "12px" }}>
                Previous scan summaries for this appointment
              </Typography>
              <Button size="small" onClick={fetchHistory} disabled={historyLoading} sx={{ textTransform: "none", fontSize: "12px", color: "neutral.teal", minWidth: "auto", p: "2px 8px" }}>
                Refresh
              </Button>
            </Box>

            {historyLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={24} sx={{ color: "neutral.teal" }} />
              </Box>
            )}

            {historyError && !historyLoading && (
              <Typography variant="body2" color="error" sx={{ p: 1.5, backgroundColor: "#fff5f5", borderRadius: "8px", textAlign: "center" }}>
                {historyError}
              </Typography>
            )}

            {!historyLoading && !historyError && !historySummary && historyImages.length === 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 6, gap: 1, color: "#9ca3af" }}>
                <ImageOutlinedIcon sx={{ fontSize: 40, opacity: 0.5 }} />
                <Typography variant="body2" color="text.disabled">No scan history found for this appointment</Typography>
              </Box>
            )}

            {!historyLoading && (historySummary || historyImages.length > 0) && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {historySummary && (
                  <Box sx={{ p: 2, backgroundColor: "#f7fbfb", borderRadius: "10px", border: "1px solid #ddeeed" }}>
                    <Typography variant="caption" sx={{ color: "neutral.teal", fontWeight: 700, fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: 0.4, display: "block", mb: 1 }}>
                      AI Summary
                    </Typography>
                    {(() => {
                      const summaries = historySummary?.summaries;
                      if (summaries && typeof summaries === "object" && !Array.isArray(summaries)) {
                        return Object.entries(summaries).map(([key, val]) => (
                          <SummarySection key={key} label={key} value={val} />
                        ));
                      }
                      return (
                        <Typography variant="body2" sx={{ color: "#1F2937", lineHeight: 1.8, whiteSpace: "pre-wrap", fontFamily: "Lato" }}>
                          {typeof historySummary === "string" ? historySummary : historySummary?.ocrContent || JSON.stringify(historySummary)}
                        </Typography>
                      );
                    })()}
                  </Box>
                )}

                {historyImages.length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 700, fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: 0.4, display: "block", mb: 1 }}>
                      Scanned Images ({historyImages.length})
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {historyImages.map((file) => (
                        <Box
                          key={file.attachment_id}
                          onClick={() => openViewer(file)}
                          sx={{ position: "relative", flexShrink: 0, cursor: "pointer" }}
                        >
                          <Box
                            component="img"
                            src={file.url}
                            alt={file.filename}
                            title={file.filename}
                            sx={{ width: 72, height: 72, objectFit: "cover", borderRadius: "8px", border: "1px solid #e0e0e0", display: "block", "&:hover": { opacity: 0.85 } }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* ── Image viewer modal ── */}
      <Modal
        open={viewerOpen}
        onClose={closeViewer}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box
          sx={{
            width: "90vw",
            height: "90vh",
            bgcolor: "background.paper",
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: 24,
            position: "relative",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              height: 54,
              px: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #E5E7EB",
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>
              {viewerFile?.filename || "Preview"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Button
                size="small"
                onClick={() => viewerFile?.url && window.open(viewerFile.url, "_blank")}
                sx={{ minWidth: 0, p: 0.5 }}
              >
                <ScreenShareOutlinedIcon sx={{ color: "neutral.teal", fontSize: 20 }} />
              </Button>
              <IconButton onClick={closeViewer}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Image */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#000",
              overflow: "hidden",
            }}
          >
            {viewerFile?.url && (
              <img
                src={viewerFile.url}
                alt={viewerFile.filename || "image"}
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
              />
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default OCRModal;
